"use client";

import React, { useState } from "react";
import { BarChart3 } from "lucide-react";

const MatchStatistics = ({ data }) => {
  const [activeTab, setActiveTab] = useState("full");

  const info = data?.info || {};

  const fullStats = data?.statistics || [];
  const firstHalfStats = data?.statistics_1half || [];

  const statistics =
    activeTab === "full" ? fullStats : firstHalfStats;

  const homeTeam = info?.match_hometeam_name || "Home";
  const awayTeam = info?.match_awayteam_name || "Away";

  // Convert value to number safely
  const getNumber = (value) => {
    if (!value) return 0;

    const cleaned = String(value).replace("%", "").trim();

    return Number(cleaned) || 0;
  };

  return (
    <div className="w-full rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm overflow-hidden mt-5 mb-5">
      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
              <BarChart3 size={20} />
              Match Statistics
            </h2>

            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Team performance comparison
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center bg-zinc-100 dark:bg-zinc-800 rounded-xl p-1">
            <button
              onClick={() => setActiveTab("full")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                activeTab === "full"
                  ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              Full Match
            </button>

            <button
              onClick={() => setActiveTab("half")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                activeTab === "half"
                  ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm"
                  : "text-zinc-500 dark:text-zinc-400"
              }`}
            >
              1st Half
            </button>
          </div>
        </div>
      </div>

      {/* No Statistics */}
      {statistics.length === 0 ? (
        <div className="py-16 px-4 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
            <BarChart3
              size={34}
              className="text-zinc-400 dark:text-zinc-500"
            />
          </div>

          <h3 className="text-lg font-semibold text-zinc-800 dark:text-zinc-200">
            No Statistics Found
          </h3>

          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Match statistics are currently unavailable.
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-5" style={{maxHeight:'500px', overflow:'auto'}}>
          {statistics.map((item, index) => {
            const homeValue = item?.home || "0";
            const awayValue = item?.away || "0";

            const homeNum = getNumber(homeValue);
            const awayNum = getNumber(awayValue);

            const total = homeNum + awayNum;

            const homeWidth =
              total > 0 ? (homeNum / total) * 100 : 50;

            const awayWidth =
              total > 0 ? (awayNum / total) * 100 : 50;

            return (
              <div key={index} className="space-y-2">
                {/* Values */}
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-[55px] text-left">
                    <p className="text-base font-bold text-zinc-900 dark:text-white">
                      {homeValue}
                    </p>

                    <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                      {homeTeam}
                    </p>
                  </div>

                  <div className="flex-1 text-center">
                    <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                      {item?.type}
                    </p>
                  </div>

                  <div className="min-w-[55px] text-right">
                    <p className="text-base font-bold text-zinc-900 dark:text-white">
                      {awayValue}
                    </p>

                    <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                      {awayTeam}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="relative w-full h-3 rounded-full overflow-hidden bg-zinc-200 dark:bg-zinc-800 flex">
                  <div
                    className="h-full bg-blue-500 transition-all duration-500"
                    style={{
                      width: `${homeWidth}%`,
                    }}
                  />

                  <div
                    className="h-full bg-red-500 transition-all duration-500"
                    style={{
                      width: `${awayWidth}%`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MatchStatistics;